/**
 * TripodBuilder - Tripod Entity 생성을 위한 Builder
 */
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d, CanvasEngineExportEntityType } from '../../../types'
import type { TripodShapePoints } from '@/app/data/type'
import { SHAPES } from '../../../const'
import type { TripodInitConfig } from './tripodEntity'

/**
 * TripodBuilder 클래스
 */
export class TripodBuilder {
  private canvasEngine: ICanvasEngineEntityController
  private config: Partial<TripodInitConfig> = {}

  constructor(canvasEngine: ICanvasEngineEntityController) {
    this.canvasEngine = canvasEngine
    this.reset()
  }

  reset(): TripodBuilder {
    this.config = { interactionMode: true }
    return this
  }

  setId(id: string): TripodBuilder {
    this.config.id = id
    return this
  }

  setCreatePoint(point: Vector2d): TripodBuilder {
    this.config.createPoint = point
    return this
  }

  setBalloonPoint(point: Vector2d): TripodBuilder {
    this.config.balloonPoint = point
    return this
  }

  setColor(color: string): TripodBuilder {
    if (!this.config.config) {
      this.config.config = { color }
    } else {
      this.config.config.color = color
    }
    return this
  }

  setTripodShapePoints(tripodShapePoints: TripodShapePoints): TripodBuilder {
    if (!this.config.config) {
      this.config.config = { color: '#000000', tripodShapePoints }
    } else {
      this.config.config.tripodShapePoints = tripodShapePoints
    }
    return this
  }

  setRotation(rotation: number): TripodBuilder {
    this.config.rotation = rotation
    return this
  }

  setLayerId(layerId: string): TripodBuilder {
    this.config.layerId = layerId
    return this
  }

  setBalloonFontColor(color: string): TripodBuilder {
    this.config.balloonFontColor = color
    return this
  }

  setBalloonFillColor(color: string): TripodBuilder {
    this.config.balloonFillColor = color
    return this
  }

  setBalloonScale(scale: number): TripodBuilder {
    this.config.balloonScale = scale
    return this
  }

  setConnectedLineThicknessScale(scale: number): TripodBuilder {
    this.config.connectedLineThicknessScale = scale
    return this
  }

  setBalloonFontScale(scale: number): TripodBuilder {
    this.config.balloonFontScale = scale
    return this
  }

  setPointShapeScale(scale: number): TripodBuilder {
    this.config.pointShapeScale = scale
    return this
  }

  setAreaBorderThicknessScale(scale: number): TripodBuilder {
    this.config.areaBorderThicknessScale = scale
    return this
  }

  setInteractionMode(mode: boolean): TripodBuilder {
    this.config.interactionMode = mode
    return this
  }

  fromExportData(data: CanvasEngineExportEntityType): TripodBuilder {
    if (data.type !== SHAPES.TRIPOD) {
      throw new Error('Invalid entity type for TripodBuilder')
    }

    this.setId(data.shapeId)
      .setCreatePoint(data.point)
      .setBalloonPoint(data.balloonPoint)
      .setColor(data.color)
      .setLayerId(data.layerId)

    if (data.tripodShapePoints) {
      this.setTripodShapePoints(data.tripodShapePoints)
    }
    if (data.rotate !== undefined) this.setRotation(data.rotate)
    if (data.balloonFontColor) this.setBalloonFontColor(data.balloonFontColor)
    if (data.balloonFillColor) this.setBalloonFillColor(data.balloonFillColor)
    if (data.balloonScale !== undefined && data.balloonScale !== null)
      this.setBalloonScale(data.balloonScale)
    if (data.connectedLineThicknessScale)
      this.setConnectedLineThicknessScale(data.connectedLineThicknessScale)
    if (data.balloonFontScale !== undefined && data.balloonFontScale !== null)
      this.setBalloonFontScale(data.balloonFontScale)
    if (data.pointShapeScale) this.setPointShapeScale(data.pointShapeScale)
    if (data.areaBorderThicknessScale)
      this.setAreaBorderThicknessScale(data.areaBorderThicknessScale)

    return this
  }

  build(): TripodInitConfig {
    if (!this.config.createPoint) {
      throw new Error('createPoint is required')
    }
    if (!this.config.config) {
      throw new Error('config is required')
    }

    return {
      id: this.config.id,
      createPoint: this.config.createPoint,
      balloonPoint: this.config.balloonPoint,
      config: this.config.config,
      rotation: this.config.rotation,
      layerId: this.config.layerId,
      balloonFontColor: this.config.balloonFontColor,
      balloonFillColor: this.config.balloonFillColor,
      balloonScale: this.config.balloonScale,
      connectedLineThicknessScale: this.config.connectedLineThicknessScale,
      balloonFontScale: this.config.balloonFontScale,
      pointShapeScale: this.config.pointShapeScale,
      areaBorderThicknessScale: this.config.areaBorderThicknessScale,
      interactionMode: this.config.interactionMode,
    }
  }

  getCanvasEngine(): ICanvasEngineEntityController {
    return this.canvasEngine
  }
}
